import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { signInPath } from '@app/constants/url';
import { useForgotPassword } from '@data';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { AuthLayout, useEnterFrom } from './AuthLayout';

// Enough to catch a typo or an empty field before the request; the address is checked for real by
// the API, and by the mail either arriving or not.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Step one of a password reset: ask for the address, then say a mail is on its way.
 *
 * The confirmation is shown whether or not that address has an account, matching what the API
 * answers. Telling someone their address is unknown would turn this form into a way to find out who
 * works here.
 */
export const ForgotPasswordPage = (): ReactElement => {
  const { t } = useTranslation();
  const enterFrom = useEnterFrom();
  const [email, setEmail] = useState('');
  const [isSent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutate: forgotPassword, isPending } = useForgotPassword();
  const isEmailValid = EMAIL_PATTERN.test(email.trim());

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    forgotPassword(
      { email: email.trim() },
      {
        onSuccess: () => setSent(true),
        onError: () => setError(t('passwordReset.requestFailed')),
      },
    );
  };

  const backToSignIn = (
    <Link
      to={signInPath}
      state={{ enterFrom: 'left' }}
      className="text-sm font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
    >
      {t('passwordReset.backToSignIn')}
    </Link>
  );

  if (isSent) {
    return (
      <AuthLayout enterFrom={enterFrom} backTo={signInPath}>
        <h1 className="mb-3 text-3xl font-bold tracking-tight text-foreground">{t('passwordReset.checkInbox')}</h1>
        <p className="mb-6 text-sm text-muted-foreground">{t('passwordReset.checkInboxHint', { email })}</p>
        <div className="flex flex-col gap-4">
          <Button variant="outline" onClick={() => setSent(false)} className="w-full">
            {t('passwordReset.useAnotherEmail')}
          </Button>
          <div className="flex justify-center">{backToSignIn}</div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout enterFrom={enterFrom} backTo={signInPath}>
      <h1 className="mb-3 text-3xl font-bold tracking-tight text-foreground">{t('passwordReset.title')}</h1>
      <p className="mb-6 text-sm text-muted-foreground">{t('passwordReset.hint')}</p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="forgot-password-email" className="sr-only">
            {t('passwordReset.email')}
          </Label>
          <Input
            id="forgot-password-email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder={t('passwordReset.email')}
            autoComplete="email"
            className="bg-popover"
            required
            autoFocus
          />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" loading={isPending} disabled={!isEmailValid} className="w-full">
          {t('passwordReset.sendLink')}
        </Button>
        <div className="flex justify-center">{backToSignIn}</div>
      </form>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
